<div class="centers-management-container">
  <mat-card>
    <mat-card-header>
      <mat-card-title>Centers Management</mat-card-title>
      <mat-card-subtitle>Manage center information and scoring points</mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <!-- Save Changes Bar - shown when editing -->
      <div *ngIf="editingCenter" class="save-bar">
        <div class="save-bar-content">
          <span class="editing-message">
            <mat-icon>edit</mat-icon>
            Editing {{ editingCenter.name }}
          </span>
          <div class="save-actions">
            <button mat-button (click)="cancelEditing()" color="warn">
              <mat-icon>cancel</mat-icon>
              Cancel
            </button>
            <button mat-raised-button
                    (click)="saveCenter()"
                    [disabled]="centerForm.invalid"
                    color="primary">
              <mat-icon>save</mat-icon>
              Save Changes
            </button>
          </div>
        </div>
      </div>

      <div class="table-container">
        <table mat-table [dataSource]="dataSource" class="centers-table">

          <!-- Center Name Column -->
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Center Name</th>
            <td mat-cell *matCellDef="let center">
              <strong>{{ center.name }}</strong>
            </td>
          </ng-container>

          <!-- OIM Name Column -->
          <ng-container matColumnDef="oimName">
            <th mat-header-cell *matHeaderCellDef>OIM Name</th>
            <td mat-cell *matCellDef="let center">
              {{ center.oimName || '-' }}
            </td>
          </ng-container>

          <!-- Full Name Column -->
          <ng-container matColumnDef="fullName">
            <th mat-header-cell *matHeaderCellDef>Full Name</th>
            <td mat-cell *matCellDef="let center">
              {{ center.fullName || '-' }}
            </td>
          </ng-container>

          <!-- Scoring Column -->
          <ng-container matColumnDef="scoring">
            <th mat-header-cell *matHeaderCellDef>
              Scoring Points
              <mat-icon class="editable-icon" matTooltip="Editable field">edit</mat-icon>
            </th>
            <td mat-cell *matCellDef="let center">
              <div *ngIf="!isEditing(center)">
                <span class="scoring-badge">{{ center.scoring || 0 }}</span>
              </div>
              <div *ngIf="isEditing(center)" [formGroup]="centerForm">
                <mat-form-field appearance="outline" class="edit-field-small">
                  <input matInput formControlName="scoring" type="number" placeholder="0">
                </mat-form-field>
              </div>
            </td>
          </ng-container>

          <!-- Max ABO Incompatible Column -->
          <ng-container matColumnDef="maxABO">
            <th mat-header-cell *matHeaderCellDef>
              Max ABO Incompatible
              <mat-icon class="editable-icon" matTooltip="Editable field">edit</mat-icon>
            </th>
            <td mat-cell *matCellDef="let center">
              <div *ngIf="!isEditing(center)">
                {{ center.MaxABOIncompatibleTransplants != null ? center.MaxABOIncompatibleTransplants : 3 }}
              </div>
              <div *ngIf="isEditing(center)" [formGroup]="centerForm">
                <mat-form-field appearance="outline" class="edit-field-small">
                  <input matInput formControlName="MaxABOIncompatibleTransplants" type="number" min="0" placeholder="3">
                  <mat-error *ngIf="centerForm.get('MaxABOIncompatibleTransplants')?.hasError('required')">
                    Required
                  </mat-error>
                  <mat-error *ngIf="centerForm.get('MaxABOIncompatibleTransplants')?.hasError('min')">
                    Must be 0 or greater
                  </mat-error>
                </mat-form-field>
              </div>
            </td>
          </ng-container>

          <!-- Max HLA Incompatible Column -->
          <ng-container matColumnDef="maxHLA">
            <th mat-header-cell *matHeaderCellDef>
              Max HLA Incompatible
              <mat-icon class="editable-icon" matTooltip="Editable field">edit</mat-icon>
            </th>
            <td mat-cell *matCellDef="let center">
              <div *ngIf="!isEditing(center)">
                {{ center.MaxHLAIncompatibleTransplants != null ? center.MaxHLAIncompatibleTransplants : 3 }}
              </div>
              <div *ngIf="isEditing(center)" [formGroup]="centerForm">
                <mat-form-field appearance="outline" class="edit-field-small">
                  <input matInput formControlName="MaxHLAIncompatibleTransplants" type="number" min="0" placeholder="3">
                  <mat-error *ngIf="centerForm.get('MaxHLAIncompatibleTransplants')?.hasError('required')">
                    Required
                  </mat-error>
                  <mat-error *ngIf="centerForm.get('MaxHLAIncompatibleTransplants')?.hasError('min')">
                    Must be 0 or greater
                  </mat-error>
                </mat-form-field>
              </div>
            </td>
          </ng-container>

          <!-- Max ABO and HLA Incompatible Column -->
          <ng-container matColumnDef="maxABOAndHLA">
            <th mat-header-cell *matHeaderCellDef>
              Max ABO+HLA Incompatible
              <mat-icon class="editable-icon" matTooltip="Editable field">edit</mat-icon>
            </th>
            <td mat-cell *matCellDef="let center">
              <div *ngIf="!isEditing(center)">
                {{ center.MaxABOAndHLAIncompatibleTransplants != null ? center.MaxABOAndHLAIncompatibleTransplants : 3 }}
              </div>
              <div *ngIf="isEditing(center)" [formGroup]="centerForm">
                <mat-form-field appearance="outline" class="edit-field-small">
                  <input matInput formControlName="MaxABOAndHLAIncompatibleTransplants" type="number" min="0" placeholder="3">
                  <mat-error *ngIf="centerForm.get('MaxABOAndHLAIncompatibleTransplants')?.hasError('required')">
                    Required
                  </mat-error>
                  <mat-error *ngIf="centerForm.get('MaxABOAndHLAIncompatibleTransplants')?.hasError('min')">
                    Must be 0 or greater
                  </mat-error>
                </mat-form-field>
              </div>
            </td>
          </ng-container>

          <!-- Participant Counts Column -->
          <ng-container matColumnDef="participantCounts">
            <th mat-header-cell *matHeaderCellDef>Participants</th>
            <td mat-cell *matCellDef="let center">
              <div class="participant-counts">
                {{ getParticipantCounts(center) }}
              </div>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let center">
              <div class="actions-container">
                <!-- Edit Button -->
                <button
                  *ngIf="!editingCenter && canEditCenters"
                  mat-icon-button
                  (click)="startEditing(center)"
                  matTooltip="Edit Scoring Points and Limits"
                  color="primary">
                  <mat-icon>edit</mat-icon>
                </button>

                <!-- Editing indicator -->
                <span *ngIf="isEditing(center)" class="editing-indicator">
                  <mat-icon color="accent">edit</mat-icon>
                  Editing
                </span>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class.editing-row]="isEditing(row)"></tr>
        </table>
      </div>

      <div *ngIf="loading" class="loading-container">
        <mat-icon class="loading-spinner">refresh</mat-icon>
        Loading centers...
      </div>

      <div *ngIf="centers.length === 0 && !loading" class="no-data">
        No centers found.
      </div>
    </mat-card-content>
  </mat-card>

  <mat-card *ngIf="canEditCenters" class="backfill-card">
    <mat-card-header>
      <mat-card-title>Backfill Participant Snapshots</mat-card-title>
      <mat-card-subtitle>One-time import of historical match run participants into the snapshot table</mat-card-subtitle>
    </mat-card-header>
    <mat-card-content>
      <p>This reads the input Excel files for all existing match runs from S3 and populates the participant_snapshot table. Already-backfilled runs are skipped. Only run this once.</p>

      <div *ngIf="backfillResult" class="backfill-result">
        <span>Processed: <strong>{{ backfillResult.processed }}</strong></span>
        <span>Skipped: <strong>{{ backfillResult.skipped }}</strong></span>
        <span>Errors: <strong>{{ backfillResult.errors }}</strong></span>
      </div>
    </mat-card-content>
    <mat-card-actions>
      <button
        mat-raised-button
        color="warn"
        [disabled]="backfillRunning"
        (click)="runBackfill()">
        <mat-spinner *ngIf="backfillRunning" diameter="18" style="display:inline-block; margin-right:8px;"></mat-spinner>
        {{ backfillRunning ? 'Running...' : 'Run Backfill' }}
      </button>
    </mat-card-actions>
  </mat-card>
</div>